import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "cn";

// shadcn/ui Button, customized at its source for the Salesko design contract.
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-solid leading-none font-semibold tracking-[var(--tracking-snug)] transition-[background,box-shadow,transform] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 enabled:active:translate-y-[0.5px] [&_svg]:shrink-0",
  { variants: {
    variant: {
      primary: "border-transparent bg-primary text-primary-foreground shadow-sm enabled:hover:bg-[var(--accent-hover)]",
      secondary: "border-input bg-card text-foreground shadow-xs enabled:hover:bg-secondary",
      ghost: "border-transparent bg-transparent text-card-foreground enabled:hover:bg-accent",
      subtle: "border-transparent bg-[var(--accent-soft)] text-[var(--accent-soft-text)] enabled:hover:bg-[var(--blue-100)]",
      danger: "border-transparent bg-destructive text-primary-foreground shadow-sm enabled:hover:bg-[var(--red-600)]",
    },
    size: {
      sm: "h-[var(--control-sm)] px-3 py-0 text-[length:var(--text-body-sm)]",
      md: "h-[var(--control-md)] px-4 py-0 text-[length:var(--text-body-md)]",
      lg: "h-[var(--control-lg)] px-[22px] py-0 text-[length:var(--text-body-lg)]",
    },
  }, defaultVariants: { variant: "primary", size: "md" } }
);

export interface ButtonProps extends React.ComponentProps<"button"> {
  variant?: "primary" | "secondary" | "ghost" | "subtle" | "danger";
  size?: "sm" | "md" | "lg";
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
}

function Button({className, variant = "primary", size = "md", iconLeft, iconRight,
  fullWidth = false, type = "button", children, ...props}: ButtonProps) {
  return <button data-slot="button" data-variant={variant} data-size={size} type={type}
    className={cn(buttonVariants({variant, size}), "leading-none", fullWidth && "w-full", className)} {...props}>
    {iconLeft}{children}{iconRight}
  </button>;
}
export { Button, buttonVariants };
